import { formatAllocabId } from './formatters';
import type { ExtranetInvoiceRide } from '../types/extranet-api.types';

/** Échappement CSV : guillemets doublés, champ cité dès qu'il porte un séparateur. */
const cell = (value: string | number): string => {
  const text = String(value ?? '');
  return /[";\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const COLUMNS = [
  'ID Allocab',
  'ID Externe',
  'Date de prise en charge',
  'Origine',
  'Destination',
  'Distance (km)',
  'Attente (min)',
  'Statut',
  'Montant (EUR)',
] as const;

const STATUS_LABELS: Record<string, string> = {
  completed: 'Effectuée',
  cancelled: 'Annulée',
  no_show: 'No-show',
};

/**
 * Listing des courses d'une facture, en CSV.
 *
 * Construit **côté client** : l'API n'expose aucune route de listing
 * (`/v1/invoices/:id/rides{,.xlsx}` répondent 404, vérifié le 11 septembre
 * 2026), mais `GET /v1/invoices` livre déjà `rides[]` en entier. Le bouton
 * pointait jusque-là sur une route inexistante et ne pouvait donc que échouer.
 *
 * Séparateur `;` et BOM UTF-8 : c'est ce qu'Excel en locale française ouvre
 * sans passer par l'assistant d'import.
 */
export const buildRidesCsv = (rides: readonly ExtranetInvoiceRide[]): Blob => {
  const lines = [
    COLUMNS.join(';'),
    ...rides.map(ride =>
      [
        formatAllocabId(ride.id),
        ride.externalRef ?? '',
        new Date(ride.pickupAt).toISOString(),
        ride.origin,
        ride.destination,
        ride.distanceKm,
        ride.waitMinutes,
        STATUS_LABELS[ride.status] ?? ride.status,
        // Le back livre des centimes.
        (ride.amount / 100).toFixed(2).replace('.', ','),
      ]
        .map(cell)
        .join(';'),
    ),
  ];
  return new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
};

/** Déclenche le téléchargement d'un blob sous le nom donné. */
export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};
